import { Fragment, useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Navigation from "../Components/Navigation";
import ClinicalHistoryCatalog from "../Components/ClinicalHistoryCatalog";
import RequireLoginComponent from "../Components/RequireLoginComponent";
import EditableField from "../Components/EditableField";
import Api from "../Services/Api";
import { Col, Container, Row, Card, Button, Badge, ListGroup, Modal, Form } from "react-bootstrap";
import dayjs from "dayjs";

interface ClinicalHistory {
  id: number;
  hasMedAllergies: number | null; // '1' = Yes, '0' = No, '-1' = Don't know, null = Not specified
  hasFoodAllergies: number | null;
  hasOtherAllergies: number | null;
  numberOfChilds: number | null;
  numberOfLivingChildren: number | null;
  numberOfBrothers: number | null;
  numberOfLivingBrothers: number | null;
  isFatherLiving: number | null;
  isMotherLiving: number | null;
  isMaternalGrandfatherLiving: number | null;
  isMaternalGrandmotherLiving: number | null;
  isPaternalGrandfatherLiving: number | null;
  isPaternalGrandmotherLiving: number | null;
  isTakingMedication: boolean | null;
  isPregnant: number | null;
  dietQuality: string | null;
  dietQuantity: string | null;
  numberOfMealsADay: number | null;
  numberOfFruitsAWeek: number | null;
  numberOfVegetablesAWeek: number | null;
  litersOfWaterADay: number | null;
  frequencyOfAlcoholAWeek: number | null;
  isDrugUser: boolean | null;
  hasSleepTreatment: number | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface ActiveSubstance {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface ActiveSubstanceAllergy {
  id: number;
  clinicalHistoryId: number;
  activeSubstance: ActiveSubstance | null;
  activeSubstanceId: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface CurrentDisease {
  id: number;
  clinicalHistoryId: number;
  cie10: CIE10 | null;
  cie10Id: number;
  activeSubstance: ActiveSubstance | null;
  activeSubstanceId: number;
  notes: number | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface CIE9 {
  id: number;
  name: string;
  code: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface Procedure {
  id: number;
  cie9: CIE9 | null;
  dateOfProcedure: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface Allergen {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface OtherAllergy {
  id: number;
  clinicalHistoryId: number;
  allergen: Allergen | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface Food {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface FoodAllergy {
  id: number;
  clinicalHistoryId: number;
  food: Food | null;
  foodId: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface CIE10 {
  id: number;
  name: string;
  code: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface HeredofamiliarDisease {
  id: number;
  cie10: CIE10 | null;
  cie10Id: number;
  numberOfChildren: number;
  isPaternalGrandfatherDiseased: number | null; // '1' = Yes, '0' = No, '-1' = Don't know
  isPaternalGrandmotherDiseased: number | null;
  isMaternalGrandfatherDiseased: number | null;
  isMaternalGrandmotherDiseased: number | null;
  numberOfSiblings: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface ActiveSubstanceUnit {
  id: number;
  name: string;
  abbreviation: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface MedicationActiveSubstance {
  id: number;
  medicationId: number;
  activeSubstanceId: number;
  activeSubstance: ActiveSubstance | null;
  activeSubstanceUnitId: number;
  activeSubstanceUnit: ActiveSubstanceUnit | null;
  quantity: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface Medication {
  id: number;
  name: string;
  medicationActiveSubstances: MedicationActiveSubstance[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface TimeIntervalUnit {
  id: number;
  name: string;
  hoursDuration: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface TreatmentPeriodicity {
  id: number;
  timeInterval: number;
  timeIntervalUnitId: number;
  timeIntervalUnit: TimeIntervalUnit | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface VivantaTreatment {
  id: number;
  name: string;
  medicationId: number;
  medication: Medication | null;
  treatmentPeriodicityId: number;
  treatmentPeriodicity: TreatmentPeriodicity | null;
  routeOfAdministration: string;
  treatmentDurationDays: number;
  isTemplate: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface UserVivantaTreatment {
  id: number;
  userId: number;
  vivantaTreatmentId: number;
  vivantaTreatment: VivantaTreatment | null;
  startDate: string;
  endDate: string;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

const VIVANTA_MORBIDITIES = ["Diabetes", "Hypertension"] as const;
type VivantaMorbidity = typeof VIVANTA_MORBIDITIES[number];

const ClinicalHistoryPage = () => {
  const { uuid, userExternalId, programId: programIdParam } = useParams<{ uuid?: string; userExternalId: string; programId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const name = searchParams.get('name');
  const programId = programIdParam ?? searchParams.get('programId');
  const userIdParam = searchParams.get('userId');
  const weightKg = parseFloat(searchParams.get('weightKg') || '0');
  const heightCm = parseFloat(searchParams.get('heightCm') || '0');
  const [data, setData] = useState(false);
  const [clinicalHistory, setClinicalHistory] = useState<ClinicalHistory | null>(null);
  const [activeSubstanceAllergies, setActiveSubstanceAllergies] = useState<ActiveSubstanceAllergy[]>([]);
  const [activeSubstanceCurrentTreatments, setActiveSubstanceCurrentTreatments] = useState<ActiveSubstanceAllergy[]>([]);
  const [currentDiseases, setCurrentDiseases] = useState<CurrentDisease[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [otherAllergies, setOtherAllergies] = useState<OtherAllergy[]>([]);
  const [foodAllergies, setFoodAllergies] = useState<FoodAllergy[]>([]);
  const [heredofamiliarDiseases, setHeredofamiliarDiseases] = useState<HeredofamiliarDisease[]>([]);
  const [userVivantaTreatments, setUserVivantaTreatments] = useState<UserVivantaTreatment[]>([]);
  const [userId, setUserId] = useState<number | null>(userIdParam ? parseInt(userIdParam) : null);
  const [loading, setLoading] = useState(true);
  const [isNotAuthenticated, setIsNotAuthenticated] = useState(false);
  
  // Modal states
  const [showOtherAllergyModal, setShowOtherAllergyModal] = useState(false);
  const [showActiveSubstanceAllergyModal, setShowActiveSubstanceAllergyModal] = useState(false);
  const [showActiveSubstanceTreatmentModal, setShowActiveSubstanceTreatmentModal] = useState(false);
  const [showCurrentDiseaseModal, setShowCurrentDiseaseModal] = useState(false);
  const [showFoodAllergyModal, setShowFoodAllergyModal] = useState(false);
  const [showProcedureModal, setShowProcedureModal] = useState(false);
  const [showHeredofamiliarDiseaseModal, setShowHeredofamiliarDiseaseModal] = useState(false);
  const [showVivantaTreatmentModal, setShowVivantaTreatmentModal] = useState(false);
  
  // Selected item states
  const [selectedAllergenId, setSelectedAllergenId] = useState<number | null>(null);
  const [selectedActiveSubstanceId, setSelectedActiveSubstanceId] = useState<number | null>(null);
  const [selectedActiveSubstanceTreatmentId, setSelectedActiveSubstanceTreatmentId] = useState<number | null>(null);
  const [selectedActiveSubstanceDiseaseId, setSelectedActiveSubstanceDiseaseId] = useState<number | null>(null);
  const [selectedFoodId, setSelectedFoodId] = useState<number | null>(null);
  const [selectedCIE9Id, setSelectedCIE9Id] = useState<number | null>(null);
  const [selectedCIE10Id, setSelectedCIE10Id] = useState<number | null>(null);
  const [selectedVivantaTreatmentId, setSelectedVivantaTreatmentId] = useState<number | null>(null);
  
  // Items for complex modals
  const [currentDiseaseSubstances, setCurrentDiseaseSubstances] = useState<ActiveSubstance[]>([]);
  const [procedureCIE9s, setProcedureCIE9s] = useState<CIE9[]>([]);
  const [heredofamiliarCIE10s, setHeredofamiliarCIE10s] = useState<CIE10[]>([]);
  const [vivantaTreatments, setVivantaTreatments] = useState<VivantaTreatment[]>([]);
  
  // Additional fields for complex modals
  const [, setCurrentDiseaseNotes] = useState<string>("");
  const [procedureDate, setProcedureDate] = useState<string>("");
  const [heredofamiliarData, setHeredofamiliarData] = useState({
    numberOfChildren: 0,
    isPaternalGrandfatherDiseased: 0,
    isPaternalGrandmotherDiseased: 0,
    isMaternalGrandfatherDiseased: 0,
    isMaternalGrandmotherDiseased: 0,
    numberOfSiblings: 0,
  });
  const [vivantaTreatmentFormData, setVivantaTreatmentFormData] = useState({
    startDate: "",
    endDate: "",
    isActive: true,
    notes: "",
  });
  
  // Debounce refs for complex modals
  const currentDiseaseSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const procedureSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heredofamiliarSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const debounceDelay = 750;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [morbiditySubmitting, setMorbiditySubmitting] = useState<VivantaMorbidity | null>(null);
  const [addedMorbidities, setAddedMorbidities] = useState<VivantaMorbidity[]>([]);
  const [programMorbidities, setProgramMorbidities] = useState<Array<{ morbidity: string; startDateOfDisease: string | null }>>([]);
  const [weightGoalPercent, setWeightGoalPercent] = useState<string>("");
  const [isSettingWeightGoal, setIsSettingWeightGoal] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    show: boolean;
    type: string;
    id: number;
    name: string;
  }>({ show: false, type: "", id: 0, name: "" });
  
  // Helper function to handle authentication errors
  const handleAuthError = useCallback((error: any) => {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      localStorage.removeItem("staffToken");
      setIsNotAuthenticated(true);
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    window.rudderanalytics?.page("Clinical History");
  }, []);

  // Check authentication token and set authentication state
  useEffect(() => {
    const token = localStorage.getItem("staffToken");
    if (!token) {
      setIsNotAuthenticated(true);
    } else {
      setIsNotAuthenticated(false);
    }
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (currentDiseaseSearchTimeoutRef.current) {
        clearTimeout(currentDiseaseSearchTimeoutRef.current);
      }
      if (procedureSearchTimeoutRef.current) {
        clearTimeout(procedureSearchTimeoutRef.current);
      }
      if (heredofamiliarSearchTimeoutRef.current) {
        clearTimeout(heredofamiliarSearchTimeoutRef.current);
      }
    };
  }, []);


  // Fetch userId from health fair participants
  useEffect(() => {
    if (uuid && userExternalId && !userId) {
      const api = new Api();
      api.getHealthFairParticipants(uuid)
        .then((participants) => {
          console.log('participants111', participants.length);
          if (participants) {
            const participant = participants.find((p: any) => p.user.externalId === userExternalId);
            if (participant && participant.user && participant.user.id) {
              setUserId(participant.user.id);
            }
          }
        })
        .catch((ex) => {
          console.log(ex);
        });
    }
  }, [uuid, userExternalId, userId]);

  useEffect(() => {
    if (!programIdParam || !userExternalId) return;
    const api = new Api();
    api.getProgramDashboard(programIdParam).then((results: any[]) => {
      if (!results) return;
      const match = results.find((r: any) => r.user?.externalId === userExternalId);
      if (match?.morbidities) {
        setProgramMorbidities(match.morbidities);
      }
    }).catch(() => {});
  }, [programIdParam, userExternalId]);

  useEffect(() => {
    if (!data && userExternalId) {
      const api = new Api();
      const clinicalHistoryData = api.getClinicalHistory(userExternalId);
      clinicalHistoryData
        .then((result) => {
          if (result) {
            setClinicalHistory(result);
            const clinicalHistoryId = result.id;
            
            // Fetch all additional data
            Promise.all([
              api.getActiveSubstanceAllergies(clinicalHistoryId).then(data => data || []),
              api.getActiveSubstanceCurrentTreatments(clinicalHistoryId).then(data => data || []),
              api.getCurrentDiseases(clinicalHistoryId).then(data => data || []),
              api.getProcedures(clinicalHistoryId).then(data => data || []),
              api.getOtherAllergies(clinicalHistoryId).then(data => data || []),
              api.getFoodAllergies(clinicalHistoryId).then(data => data || []),
              api.getHeredofamiliarDiseases(clinicalHistoryId).then(data => data || []),
              api.getUserVivantaTreatments(userExternalId).then(data => data || []),
              api.getTreatments().then(data => data || []),
            ]).then(([
              allergies,
              treatments,
              diseases,
              proceduresData,
              otherAllergiesData,
              foodAllergiesData,
              heredofamiliarDiseasesData,
              vivantaTreatmentsData,
              treatmentsList,
            ]) => {
              setActiveSubstanceAllergies(allergies);
              setActiveSubstanceCurrentTreatments(treatments);
              setCurrentDiseases(diseases);
              setProcedures(proceduresData);
              setOtherAllergies(otherAllergiesData);
              setFoodAllergies(foodAllergiesData);
              setHeredofamiliarDiseases(heredofamiliarDiseasesData);
              setUserVivantaTreatments(vivantaTreatmentsData);
              setVivantaTreatments(treatmentsList);
              setLoading(false);
            }).catch((ex) => {
              console.log(ex);
              if (!handleAuthError(ex)) {
                setLoading(false);
              }
            });
          } else {
            setLoading(false);
          }
        })
        .catch((ex) => {
          console.log(ex);
          if (!handleAuthError(ex)) {
            setLoading(false);
          }
        });
      setData(true);
    }
  }, [data, userExternalId, navigate, handleAuthError]);

  const formatDateTime = (dateString: string): string => {
    return dayjs(dateString).format("DD-MM-YYYY HH:mm");
  };

  const formatDate = (dateString: string): string => {
    return dayjs(dateString).format("DD-MM-YYYY");
  };

  const formatEnumBadge = (value: number | null) => {
    if (value === null || value === undefined) return <Badge bg="secondary">Not specified</Badge>;
    if (value === 1) return <Badge bg="success">Yes</Badge>;
    if (value === 0) return <Badge bg="danger">No</Badge>;
    if (value === -1) return <Badge bg="warning" text="dark">Don't know</Badge>;
    return <Badge bg="secondary">Not specified</Badge>;
  };

  const formatEnumDisplay = (value: string | number | null): string => {
    if (value === null || value === undefined) return "Not specified";
    const numValue = typeof value === 'string' ? parseInt(value) : value;
    if (numValue === 1) return "Yes";
    if (numValue === 0) return "No";
    if (numValue === -1) return "Don't know";
    return "Not specified";
  };

  const formatVivantaTreatmentText = (treatment: VivantaTreatment): string => {
    if (!treatment.medication) {
      return treatment.name;
    }

    const medicationName = treatment.medication.name;
    
    // Format active substances: "metformina 800mg, empaglifozina 12.5mg"
    const activeSubstances = treatment.medication.medicationActiveSubstances
      .map((mas) => {
        const substanceName = mas.activeSubstance?.name?.toLowerCase() || "";
        // Remove trailing zeros from quantity (e.g., "800.0000" -> "800", "12.5000" -> "12.5")
        const quantity = parseFloat(mas.quantity).toString();
        const unit = mas.activeSubstanceUnit?.abbreviation?.toLowerCase() || "";
        return `${substanceName} ${quantity}${unit}`;
      })
      .join(", ");

    // Format periodicity: "tomar 1 cada 8 horas" or "tomar 1 cada día"
    const timeInterval = treatment.treatmentPeriodicity?.timeInterval || 1;
    const timeIntervalUnit = treatment.treatmentPeriodicity?.timeIntervalUnit;
    let timeUnitText = "día";
    
    if (timeIntervalUnit) {
      // If hoursDuration is less than 24, use "horas", otherwise use the unit name
      if (timeIntervalUnit.hoursDuration < 24) {
        timeUnitText = `${timeIntervalUnit.hoursDuration} horas`;
      } else {
        timeUnitText = timeIntervalUnit.name;
      }
    }
    
    const periodicityText = `tomar ${timeInterval} cada ${timeUnitText}`;

    // Format duration: "por 90 días"
    const durationText = `por ${treatment.treatmentDurationDays} días`;

    // Combine: "Jardianz Duo (metformina 800mg, empaglifozina 12.5mg) tomar 1 cada día por 90 días."
    return `${medicationName} (${activeSubstances}) ${periodicityText} ${durationText}.`;
  };

  // Other Allergy handlers
  const handleOpenOtherAllergyModal = () => {
    setShowOtherAllergyModal(true);
    setSelectedAllergenId(null);
  };

  const handleCloseOtherAllergyModal = () => {
    setShowOtherAllergyModal(false);
    setSelectedAllergenId(null);
  };

  const handleSearchAllergens = async (searchValue: string): Promise<Allergen[]> => {
    const api = new Api();
    const result = await api.searchOtherAllergies(searchValue);
    return result || [];
  };

  const handleGetAllAllergens = async (): Promise<Allergen[]> => {
    const api = new Api();
    const result = await api.getAllAllergens();
    return result || [];
  };

  const handleSubmitOtherAllergy = async () => {
    if (!selectedAllergenId || !clinicalHistory) return;
    
    setIsSubmitting(true);
    const api = new Api();
    try {
      await api.createOtherAllergy(clinicalHistory.id, selectedAllergenId);
      const updatedAllergies = await api.getOtherAllergies(clinicalHistory.id);
      if (updatedAllergies) {
        setOtherAllergies(updatedAllergies);
      }
      handleCloseOtherAllergyModal();
    } catch (ex) {
      console.log(ex);
      handleAuthError(ex);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Active Substance Allergy handlers
  const handleOpenActiveSubstanceAllergyModal = () => {
    setShowActiveSubstanceAllergyModal(true);
    setSelectedActiveSubstanceId(null);
  };

  const handleCloseActiveSubstanceAllergyModal = () => {
    setShowActiveSubstanceAllergyModal(false);
    setSelectedActiveSubstanceId(null);
  };

  const handleSearchActiveSubstances = async (searchValue: string): Promise<ActiveSubstance[]> => {
    const api = new Api();
    const result = await api.searchActiveSubstances(searchValue);
    return result || [];
  };

  const handleGetAllActiveSubstances = async (): Promise<ActiveSubstance[]> => {
    const api = new Api();
    const result = await api.getAllActiveSubstances();
    return result || [];
  };

  const handleSubmitActiveSubstanceAllergy = async () => {
    if (!selectedActiveSubstanceId || !clinicalHistory) return;
    
    setIsSubmitting(true);
    const api = new Api();
    try {
      await api.createActiveSubstanceAllergy(clinicalHistory.id, selectedActiveSubstanceId);
      const updated = await api.getActiveSubstanceAllergies(clinicalHistory.id);
      if (updated) {
        setActiveSubstanceAllergies(updated);
      }
      handleCloseActiveSubstanceAllergyModal();
    } catch (ex) {
      console.log(ex);
      handleAuthError(ex);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Active Substance Current Treatment handlers
  const handleOpenActiveSubstanceTreatmentModal = () => {
    setShowActiveSubstanceTreatmentModal(true);
    setSelectedActiveSubstanceTreatmentId(null);
  };

  const handleCloseActiveSubstanceTreatmentModal = () => {
    setShowActiveSubstanceTreatmentModal(false);
    setSelectedActiveSubstanceTreatmentId(null);
  };

  const handleSubmitActiveSubstanceTreatment = async () => {
    if (!selectedActiveSubstanceTreatmentId || !clinicalHistory) return;
    
    setIsSubmitting(true);
    const api = new Api();
    try {
      await api.createActiveSubstanceCurrentTreatment(clinicalHistory.id, selectedActiveSubstanceTreatmentId);
      const updated = await api.getActiveSubstanceCurrentTreatments(clinicalHistory.id);
      if (updated) {
        setActiveSubstanceCurrentTreatments(updated);
      }
      handleCloseActiveSubstanceTreatmentModal();
    } catch (ex) {
      console.log(ex);
      handleAuthError(ex);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Food Allergy handlers
  const handleOpenFoodAllergyModal = () => {
    setShowFoodAllergyModal(true);
    setSelectedFoodId(null);
  };

  const handleCloseFoodAllergyModal = () => {
    setShowFoodAllergyModal(false);
    setSelectedFoodId(null);
  };

  const handleSearchFoods = async (searchValue: string): Promise<Food[]> => {
    const api = new Api();
    const result = await api.searchFoods(searchValue);
    return result || [];
  };

  const handleGetAllFoods = async (): Promise<Food[]> => {
    const api = new Api();
    const result = await api.getAllFoods();
    return result || [];
  };

  const handleSubmitFoodAllergy = async () => {
    if (!selectedFoodId || !clinicalHistory) return;
    
    setIsSubmitting(true);
    const api = new Api();
    try {
      await api.createFoodAllergy(clinicalHistory.id, selectedFoodId);
      const updated = await api.getFoodAllergies(clinicalHistory.id);
      if (updated) {
        setFoodAllergies(updated);
      }
      handleCloseFoodAllergyModal();
    } catch (ex) {
      console.log(ex);
      handleAuthError(ex);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Current Disease handlers (with notes)
  const handleOpenCurrentDiseaseModal = () => {
    setShowCurrentDiseaseModal(true);
    setSelectedActiveSubstanceDiseaseId(null);
    setCurrentDiseaseNotes("");
    handleGetAllCIE10().then((result) => {
      setCurrentDiseaseSubstances(result);
    });
  };

  const handleCloseCurrentDiseaseModal = () => {
    setShowCurrentDiseaseModal(false);
    setSelectedActiveSubstanceDiseaseId(null);
    setCurrentDiseaseNotes("");
    // Clear any pending search timeout
    if (currentDiseaseSearchTimeoutRef.current) {
      clearTimeout(currentDiseaseSearchTimeoutRef.current);
      currentDiseaseSearchTimeoutRef.current = null;
    }
  };

  const handleSubmitCurrentDisease = async () => {
    if (!selectedCIE10Id || !clinicalHistory) return;
    
    setIsSubmitting(true);
    const api = new Api();
    try {
      await api.createCurrentDisease(
        clinicalHistory.id,
        selectedCIE10Id,
        null
      );
      const updated = await api.getCurrentDiseases(clinicalHistory.id);
      if (updated) {
        setCurrentDiseases(updated);
      }
      handleCloseCurrentDiseaseModal();
    } catch (ex) {
      console.log(ex);
      handleAuthError(ex);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Procedure handlers (with date)
  const handleOpenProcedureModal = () => {
    setShowProcedureModal(true);
    setSelectedCIE9Id(null);
    setProcedureDate("");
    handleGetAllCIE9().then((result) => {
      setProcedureCIE9s(result);
    });
  };

  const handleCloseProcedureModal = () => {
    setShowProcedureModal(false);
    setSelectedCIE9Id(null);
    setProcedureDate("");
    // Clear any pending search timeout
    if (procedureSearchTimeoutRef.current) {
      clearTimeout(procedureSearchTimeoutRef.current);
      procedureSearchTimeoutRef.current = null;
    }
  };

  const handleSearchCIE9 = async (searchValue: string): Promise<CIE9[]> => {
    const api = new Api();
    const result = await api.searchCIE9(searchValue);
    return result || [];
  };

  const handleGetAllCIE9 = async (): Promise<CIE9[]> => {
    const api = new Api();
    const result = await api.getAllCIE9();
    return result || [];
  };

  const handleSubmitProcedure = async () => {
    if (!selectedCIE9Id || !procedureDate || !clinicalHistory) return;
    
    setIsSubmitting(true);
    const api = new Api();
    try {
      await api.createProcedure(clinicalHistory.id, selectedCIE9Id, procedureDate);
      const updated = await api.getProcedures(clinicalHistory.id);
      if (updated) {
        setProcedures(updated);
      }
      handleCloseProcedureModal();
    } catch (ex) {
      console.log(ex);
      handleAuthError(ex);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Heredofamiliar Disease handlers (complex form)
  const handleOpenHeredofamiliarDiseaseModal = () => {
    setShowHeredofamiliarDiseaseModal(true);
    setSelectedCIE10Id(null);
    setHeredofamiliarData({
      numberOfChildren: 0,
      isPaternalGrandfatherDiseased: 0,
      isPaternalGrandmotherDiseased: 0,
      isMaternalGrandfatherDiseased: 0,
      isMaternalGrandmotherDiseased: 0,
      numberOfSiblings: 0,
    });
    handleGetAllCIE10().then((result) => {
      setHeredofamiliarCIE10s(result);
    });
  };

  const handleCloseHeredofamiliarDiseaseModal = () => {
    setShowHeredofamiliarDiseaseModal(false);
    setSelectedCIE10Id(null);
    setHeredofamiliarData({
      numberOfChildren: 0,
      isPaternalGrandfatherDiseased: 0,
      isPaternalGrandmotherDiseased: 0,
      isMaternalGrandfatherDiseased: 0,
      isMaternalGrandmotherDiseased: 0,
      numberOfSiblings: 0,
    });
    // Clear any pending search timeout
    if (heredofamiliarSearchTimeoutRef.current) {
      clearTimeout(heredofamiliarSearchTimeoutRef.current);
      heredofamiliarSearchTimeoutRef.current = null;
    }
  };

  const handleSearchCIE10 = async (searchValue: string): Promise<CIE10[]> => {
    const api = new Api();
    const result = await api.searchCIE10(searchValue);
    return result || [];
  };

  const handleGetAllCIE10 = async (): Promise<CIE10[]> => {
    const api = new Api();
    const result = await api.getAllCIE10();
    return result || [];
  };

  const handleSubmitHeredofamiliarDisease = async () => {
    if (!selectedCIE10Id || !clinicalHistory) return;
    
    setIsSubmitting(true);
    const api = new Api();
    try {
      await api.createHeredofamiliarDisease(
        clinicalHistory.id,
        selectedCIE10Id,
        heredofamiliarData.numberOfChildren,
        heredofamiliarData.isPaternalGrandfatherDiseased,
        heredofamiliarData.isPaternalGrandmotherDiseased,
        heredofamiliarData.isMaternalGrandfatherDiseased,
        heredofamiliarData.isMaternalGrandmotherDiseased,
        heredofamiliarData.numberOfSiblings
      );
      const updated = await api.getHeredofamiliarDiseases(clinicalHistory.id);
      if (updated) {
        setHeredofamiliarDiseases(updated);
      }
      handleCloseHeredofamiliarDiseaseModal();
    } catch (ex) {
      console.log(ex);
      handleAuthError(ex);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Vivanta Treatment handlers
  const handleOpenVivantaTreatmentModal = () => {
    setShowVivantaTreatmentModal(true);
    setSelectedVivantaTreatmentId(null);
    setVivantaTreatmentFormData({
      startDate: "",
      endDate: "",
      isActive: true,
      notes: "",
    });
  };

  const handleCloseVivantaTreatmentModal = () => {
    setShowVivantaTreatmentModal(false);
    setSelectedVivantaTreatmentId(null);
    setVivantaTreatmentFormData({
      startDate: "",
      endDate: "",
      isActive: true,
      notes: "",
    });
  };

  const handleSubmitVivantaTreatment = async () => {
    if (!selectedVivantaTreatmentId || !userId || !userExternalId) return;
    
    setIsSubmitting(true);
    const api = new Api();
    try {
      await api.createUserVivantaTreatment({
        userId: userId,
        vivantaTreatmentId: selectedVivantaTreatmentId,
        startDate: vivantaTreatmentFormData.startDate,
        endDate: vivantaTreatmentFormData.endDate,
        isActive: vivantaTreatmentFormData.isActive,
        notes: vivantaTreatmentFormData.notes || undefined,
      });
      const updated = await api.getUserVivantaTreatments(userExternalId);
      if (updated) {
        setUserVivantaTreatments(updated);
      }
      handleCloseVivantaTreatmentModal();
    } catch (ex) {
      console.log(ex);
      handleAuthError(ex);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete handlers
  const handleDeleteClick = (type: string, id: number, name: string) => {
    setDeleteConfirmModal({ show: true, type, id, name });
  };

  const handleDeleteConfirm = async () => {
    if (!clinicalHistory) return;
    
    setIsSubmitting(true);
    const api = new Api();
    try {
      switch (deleteConfirmModal.type) {
        case "activeSubstanceAllergy":
          await api.deleteActiveSubstanceAllergy(clinicalHistory.id, deleteConfirmModal.id);
          const updatedAllergies = await api.getActiveSubstanceAllergies(clinicalHistory.id);
          if (updatedAllergies) setActiveSubstanceAllergies(updatedAllergies);
          break;
        case "activeSubstanceTreatment":
          await api.deleteActiveSubstanceCurrentTreatment(clinicalHistory.id, deleteConfirmModal.id);
          const updatedTreatments = await api.getActiveSubstanceCurrentTreatments(clinicalHistory.id);
          if (updatedTreatments) setActiveSubstanceCurrentTreatments(updatedTreatments);
          break;
        case "currentDisease":
          await api.deleteCurrentDisease(clinicalHistory.id, deleteConfirmModal.id);
          const updatedDiseases = await api.getCurrentDiseases(clinicalHistory.id);
          if (updatedDiseases) setCurrentDiseases(updatedDiseases);
          break;
        case "procedure":
          await api.deleteProcedure(clinicalHistory.id, deleteConfirmModal.id);
          const updatedProcedures = await api.getProcedures(clinicalHistory.id);
          if (updatedProcedures) setProcedures(updatedProcedures);
          break;
        case "foodAllergy":
          await api.deleteFoodAllergy(clinicalHistory.id, deleteConfirmModal.id);
          const updatedFoodAllergies = await api.getFoodAllergies(clinicalHistory.id);
          if (updatedFoodAllergies) setFoodAllergies(updatedFoodAllergies);
          break;
        case "otherAllergy":
          await api.deleteOtherAllergy(clinicalHistory.id, deleteConfirmModal.id);
          const updatedOtherAllergies = await api.getOtherAllergies(clinicalHistory.id);
          if (updatedOtherAllergies) setOtherAllergies(updatedOtherAllergies);
          break;
        case "heredofamiliarDisease":
          await api.deleteHeredofamiliarDisease(clinicalHistory.id, deleteConfirmModal.id);
          const updatedHeredofamiliar = await api.getHeredofamiliarDiseases(clinicalHistory.id);
          if (updatedHeredofamiliar) setHeredofamiliarDiseases(updatedHeredofamiliar);
          break;
        case "vivantaTreatment":
          await api.deleteUserVivantaTreatment(deleteConfirmModal.id);
          if (userExternalId) {
            const updatedVivantaTreatments = await api.getUserVivantaTreatments(userExternalId);
            if (updatedVivantaTreatments) setUserVivantaTreatments(updatedVivantaTreatments);
          }
          break;
      }
      setDeleteConfirmModal({ show: false, type: "", id: 0, name: "" });
    } catch (ex) {
      console.log(ex);
      if (!handleAuthError(ex)) {
        setIsSubmitting(false);
      }
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmModal({ show: false, type: "", id: 0, name: "" });
  };

  // Update clinical history field
  const handleUpdateClinicalHistory = async (field: string, value: string | number | boolean | null) => {
    if (!clinicalHistory) return;
    
    setIsSubmitting(true);
    const api = new Api();
    try {
      await api.updateClinicalHistory(clinicalHistory.id, { [field]: value });
      // Refresh clinical history data
      const updated = await api.getClinicalHistory(userExternalId!);
      if (updated) {
        setClinicalHistory(updated);
      }
    } catch (ex) {
      console.log(ex);
      handleAuthError(ex);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddMorbidity = async (morbidity: VivantaMorbidity) => {
    if (!programId || !userExternalId) return;
    setMorbiditySubmitting(morbidity);
    const api = new Api();
    try {
      await api.addPatientMorbidity(programId, userExternalId, morbidity);
      setAddedMorbidities((prev) => [...prev, morbidity]);
    } catch (ex) {
      console.log(ex);
      handleAuthError(ex);
    } finally {
      setMorbiditySubmitting(null);
    }
  };

  const enumOptions = [
    { value: 1, label: "Yes" },
    { value: 0, label: "No" },
    { value: -1, label: "Don't know" },
  ];

  const dietQuantityOptions = [
    { value: "rich", label: "Rich" },
    { value: "regular", label: "Regular" },
    { value: "poor", label: "Poor" },
  ];

  const dietQualityOptions = [
    { value: "good", label: "Good" },
    { value: "regular", label: "Regular" },
    { value: "bad", label: "Bad" },
  ];

  if (loading) {
    return (
      <Fragment>
        <Navigation />
        <Container className="mt-4">
          <RequireLoginComponent isNotAuthenticated={isNotAuthenticated} />
          <Row>
            <Col>
              <p className="text-muted"><strong>Name:</strong> {name}</p>
              <p>Loading clinical history...</p>
            </Col>
          </Row>
        </Container>
      </Fragment>
    );
  }

  if (!clinicalHistory) {
    return (
      <Fragment>
        <Navigation />
        <Container className="mt-4">
          <RequireLoginComponent isNotAuthenticated={isNotAuthenticated} />
          <Row>
            <Col>
              <Button
                variant="secondary"
                onClick={() => programIdParam
                  ? navigate(`/programs/${programIdParam}/dashboard`)
                  : navigate(`/health-fairs/${uuid}/participants`)
                }
                className="mb-3"
              >
                {programIdParam ? "← Back to Dashboard" : "← Back to Participants"}
              </Button>
              <h1>Clinical History</h1>
              <p className="text-muted">User External ID: {userExternalId}</p>
              <p className="text-muted"><strong>Name:</strong> {name}</p>
              <p className="text-danger">No clinical history data found.</p>
            </Col>
          </Row>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Navigation />
      <Container className="mt-4">
        <RequireLoginComponent isNotAuthenticated={isNotAuthenticated} />
        <Row>
          <Col>
            <Button
              variant="secondary"
              onClick={() => programIdParam
                ? navigate(`/programs/${programIdParam}/dashboard`)
                : navigate(`/health-fairs/${uuid}/participants`)
              }
              className="mb-3"
            >
              {programIdParam ? "← Back to Dashboard" : "← Back to Participants"}
            </Button>
            <h1>Clinical History</h1>
            <p className="text-muted">User External ID: {userExternalId}</p>
            <p className="text-muted"><strong>Name:</strong> {name}</p>
            <p className="text-muted"><strong>Weight:</strong> {weightKg}</p>
            <p className="text-muted d-flex align-items-center gap-2 flex-wrap">
              <strong>Weight goal:</strong>
              <Form.Control
                type="number"
                min={0}
                max={100}
                placeholder="%"
                value={weightGoalPercent}
                onChange={(e) => setWeightGoalPercent(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
                style={{ width: 70 }}
                className="d-inline-block"
              />
              {weightGoalPercent && !isNaN(parseInt(weightGoalPercent)) && weightKg > 0 && (
                <>
                  <span>
                    → {Math.round(weightKg * (1 - parseInt(weightGoalPercent) / 100) * 10) / 10} kg
                  </span>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!userExternalId || isSettingWeightGoal}
                    onClick={async () => {
                      if (!userExternalId) return;
                      const percent = parseInt(weightGoalPercent);
                      if (isNaN(percent) || percent < 0 || percent > 100 || weightKg <= 0) return;
                      const targetWeight = Math.round(weightKg * (1 - percent / 100) * 10) / 10;
                      setIsSettingWeightGoal(true);
                      try {
                        const api = new Api();
                        await api.createWeightGoal(userExternalId, {
                          weightGoal: targetWeight,
                          fromDate: dayjs().format("YYYY-MM-DD"),
                          toDate: dayjs().add(6, "months").format("YYYY-MM-DD"),
                        });
                      } finally {
                        setIsSettingWeightGoal(false);
                      }
                    }}
                  >
                    {isSettingWeightGoal ? "Setting…" : "Set goal"}
                  </Button>
                </>
              )}
            </p>
            <p className="text-muted"><strong>Weight for BMI 25: </strong>{Math.round(25 * (heightCm / 100) * (heightCm / 100))} kg</p>
            <p className="text-muted"><strong>Height:</strong> {heightCm}</p>

          </Col>
        </Row>

        <Row className="mt-4">
          {/* Metadata Section */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-light text-dark">
                <h5 className="mb-0">Record Information</h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-2">
                  <Col sm={6}><strong>Created At:</strong></Col>
                  <Col sm={6}>{formatDateTime(clinicalHistory.createdAt)}</Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={6}><strong>Updated At:</strong></Col>
                  <Col sm={6}>{formatDateTime(clinicalHistory.updatedAt)}</Col>
                </Row>
                <Row>
                  <Col sm={6}><strong>Record ID:</strong></Col>
                  <Col sm={6}>{clinicalHistory.id}</Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} className="mb-4"></Col>
          {/* Allergies Section */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">Allergies</h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-2">
                  <Col sm={6}><strong>Medical Allergies:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Medical Allergies"
                      value={clinicalHistory.hasMedAllergies}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => handleUpdateClinicalHistory("hasMedAllergies", value)}
                      formatDisplay={formatEnumDisplay}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={6}><strong>Food Allergies:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Food Allergies"
                      value={clinicalHistory.hasFoodAllergies}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => handleUpdateClinicalHistory("hasFoodAllergies", value)}
                      formatDisplay={formatEnumDisplay}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}><strong>Other Allergies:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Other Allergies"
                      value={clinicalHistory.hasOtherAllergies}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => handleUpdateClinicalHistory("hasOtherAllergies", value)}
                      formatDisplay={formatEnumDisplay}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Active Substance Allergies Detail */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-danger text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Active Substance Allergies</h5>
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleOpenActiveSubstanceAllergyModal}
                  style={{ fontSize: '1.2rem', lineHeight: '1', padding: '0.25rem 0.5rem' }}
                >
                  +
                </Button>
              </Card.Header>
              <Card.Body>
                {activeSubstanceAllergies.length > 0 ? (
                  <ListGroup variant="flush">
                    {activeSubstanceAllergies.map((allergy) => (
                      <ListGroup.Item key={allergy.id} className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{allergy.activeSubstance?.name || "Unknown"}</strong>
                          {allergy.activeSubstance?.id && (
                            <span className="text-muted ms-2">(ID: {allergy.activeSubstance.id})</span>
                          )}
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteClick("activeSubstanceAllergy", allergy.id, allergy.activeSubstance?.name || "Unknown")}
                        >
                          ×
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <p className="text-muted mb-0">No active substance allergies recorded</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Food Allergies Detail */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Food Allergies Detail</h5>
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleOpenFoodAllergyModal}
                  style={{ fontSize: '1.2rem', lineHeight: '1', padding: '0.25rem 0.5rem' }}
                >
                  +
                </Button>
              </Card.Header>
              <Card.Body>
                {foodAllergies.length > 0 ? (
                  <ListGroup variant="flush">
                    {foodAllergies.map((allergy) => (
                      <ListGroup.Item key={allergy.id} className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{allergy.food?.name || "Unknown"}</strong>
                          {allergy.food?.id && (
                            <span className="text-muted ms-2">(ID: {allergy.food.id})</span>
                          )}
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteClick("foodAllergy", allergy.id, allergy.food?.name || "Unknown")}
                        >
                          ×
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <p className="text-muted mb-0">No food allergies recorded</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Other Allergies Detail */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Other Allergies Detail</h5>
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleOpenOtherAllergyModal}
                  style={{ fontSize: '1.2rem', lineHeight: '1', padding: '0.25rem 0.5rem' }}
                >
                  +
                </Button>
              </Card.Header>
              <Card.Body>
                {otherAllergies.length > 0 ? (
                  <ListGroup variant="flush">
                    {otherAllergies.map((allergy) => (
                      <ListGroup.Item key={allergy.id} className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{allergy.allergen?.name || "Unknown"}</strong>
                          {allergy.allergen?.id && (
                            <span className="text-muted ms-2">(ID: {allergy.allergen.id})</span>
                          )}
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteClick("otherAllergy", allergy.id, allergy.allergen?.name || "Unknown")}
                        >
                          ×
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <p className="text-muted mb-0">No other allergies recorded</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Children Section */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0">Children</h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-2">
                  <Col sm={6}><strong>Number of Children:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Number of Children"
                      value={clinicalHistory.numberOfChilds}
                      type="number"
                      onSave={(value) => handleUpdateClinicalHistory("numberOfChilds", value)}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}><strong>Living Children:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Living Children"
                      value={clinicalHistory.numberOfLivingChildren}
                      type="number"
                      onSave={(value) => handleUpdateClinicalHistory("numberOfLivingChildren", value)}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Family History Section */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-info text-white">
                <h5 className="mb-0">Family History</h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-2">
                  <Col sm={6}><strong>Father Living:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Father Living"
                      value={clinicalHistory.isFatherLiving}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => handleUpdateClinicalHistory("isFatherLiving", value)}
                      formatDisplay={formatEnumDisplay}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={6}><strong>Mother Living:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Mother Living"
                      value={clinicalHistory.isMotherLiving}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => handleUpdateClinicalHistory("isMotherLiving", value)}
                      formatDisplay={formatEnumDisplay}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={6}><strong>Maternal Grandfather:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Maternal Grandfather"
                      value={clinicalHistory.isMaternalGrandfatherLiving}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => handleUpdateClinicalHistory("isMaternalGrandfatherLiving", value)}
                      formatDisplay={formatEnumDisplay}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={6}><strong>Maternal Grandmother:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Maternal Grandmother"
                      value={clinicalHistory.isMaternalGrandmotherLiving}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => handleUpdateClinicalHistory("isMaternalGrandmotherLiving", value)}
                      formatDisplay={formatEnumDisplay}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={6}><strong>Paternal Grandfather:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Paternal Grandfather"
                      value={clinicalHistory.isPaternalGrandfatherLiving}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => handleUpdateClinicalHistory("isPaternalGrandfatherLiving", value)}
                      formatDisplay={formatEnumDisplay}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={6}><strong>Paternal Grandmother:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Paternal Grandmother"
                      value={clinicalHistory.isPaternalGrandmotherLiving}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => handleUpdateClinicalHistory("isPaternalGrandmotherLiving", value)}
                      formatDisplay={formatEnumDisplay}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={6}><strong>Number of Brothers:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Number of Brothers"
                      value={clinicalHistory.numberOfBrothers}
                      type="number"
                      onSave={(value) => handleUpdateClinicalHistory("numberOfBrothers", value)}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}><strong>Living Brothers:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Living Brothers"
                      value={clinicalHistory.numberOfLivingBrothers}
                      type="number"
                      onSave={(value) => handleUpdateClinicalHistory("numberOfLivingBrothers", value)}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Heredofamiliar Diseases */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Heredofamiliar Diseases</h5>
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleOpenHeredofamiliarDiseaseModal}
                  style={{ fontSize: '1.2rem', lineHeight: '1', padding: '0.25rem 0.5rem' }}
                >
                  +
                </Button>
              </Card.Header>
              <Card.Body>
                {heredofamiliarDiseases.length > 0 ? (
                  <ListGroup variant="flush">
                    {heredofamiliarDiseases.map((disease) => (
                      <ListGroup.Item key={disease.id} className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="mb-2">
                            <strong>{disease.cie10?.name || "Unknown"}</strong>
                            {disease.cie10?.code && (
                              <Badge bg="primary" className="ms-2">{disease.cie10.code}</Badge>
                            )}
                          </div>
                          <div className="small">
                            <Row className="mb-1">
                              <Col sm={6}>Paternal Grandfather:</Col>
                              <Col sm={6}>{formatEnumBadge(disease.isPaternalGrandfatherDiseased)}</Col>
                            </Row>
                            <Row className="mb-1">
                              <Col sm={6}>Paternal Grandmother:</Col>
                              <Col sm={6}>{formatEnumBadge(disease.isPaternalGrandmotherDiseased)}</Col>
                            </Row>
                            <Row className="mb-1">
                              <Col sm={6}>Maternal Grandfather:</Col>
                              <Col sm={6}>{formatEnumBadge(disease.isMaternalGrandfatherDiseased)}</Col>
                            </Row>
                            <Row className="mb-1">
                              <Col sm={6}>Maternal Grandmother:</Col>
                              <Col sm={6}>{formatEnumBadge(disease.isMaternalGrandmotherDiseased)}</Col>
                            </Row>
                            <Row className="mb-1">
                              <Col sm={6}>Number of Children:</Col>
                              <Col sm={6}>{disease.numberOfChildren}</Col>
                            </Row>
                            <Row>
                              <Col sm={6}>Number of Siblings:</Col>
                              <Col sm={6}>{disease.numberOfSiblings}</Col>
                            </Row>
                          </div>
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteClick("heredofamiliarDisease", disease.id, disease.cie10?.name || "Unknown")}
                          className="ms-2"
                        >
                          ×
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <p className="text-muted mb-0">No heredofamiliar diseases recorded</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Medications & Conditions Section */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-warning text-dark">
                <h5 className="mb-0">Medications & Conditions</h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-2">
                  <Col sm={6}><strong>Taking Medication:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Taking Medication"
                      value={clinicalHistory.isTakingMedication === true ? 1 : (clinicalHistory.isTakingMedication === false ? 0 : null)}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => {
                        const boolValue = value === 1 ? true : (value === 0 ? false : null);
                        return handleUpdateClinicalHistory("isTakingMedication", boolValue);
                      }}
                      formatDisplay={(value) => {
                        const numValue = typeof value === 'string' ? parseInt(value) : value;
                        if (numValue === 1) return "Yes";
                        if (numValue === 0) return "No";
                        return "Not specified";
                      }}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}><strong>Pregnant:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Pregnant"
                      value={clinicalHistory.isPregnant}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => handleUpdateClinicalHistory("isPregnant", value)}
                      formatDisplay={formatEnumDisplay}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Active Substance Current Treatments */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Current Treatments</h5>
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleOpenActiveSubstanceTreatmentModal}
                  style={{ fontSize: '1.2rem', lineHeight: '1', padding: '0.25rem 0.5rem' }}
                >
                  +
                </Button>
              </Card.Header>
              <Card.Body>
                {activeSubstanceCurrentTreatments.length > 0 ? (
                  <ListGroup variant="flush">
                    {activeSubstanceCurrentTreatments.map((treatment) => (
                      <ListGroup.Item key={treatment.id} className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{treatment.activeSubstance?.name || "Unknown"}</strong>
                          {treatment.activeSubstance?.id && (
                            <span className="text-muted ms-2">(ID: {treatment.activeSubstance.id})</span>
                          )}
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteClick("activeSubstanceTreatment", treatment.id, treatment.activeSubstance?.name || "Unknown")}
                        >
                          ×
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <p className="text-muted mb-0">No current treatments recorded</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Vivanta Treatments */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Vivanta Treatments</h5>
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleOpenVivantaTreatmentModal}
                  style={{ fontSize: '1.2rem', lineHeight: '1', padding: '0.25rem 0.5rem' }}
                >
                  +
                </Button>
              </Card.Header>
              <Card.Body>
                {userVivantaTreatments.length > 0 ? (
                  <ListGroup variant="flush">
                    {userVivantaTreatments.map((treatment: any) => (
                      <ListGroup.Item key={treatment.userVivantaTreatmentId || treatment.id} className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="mb-2">
                            {treatment.medication ? (
                              <div>
                                <strong>{formatVivantaTreatmentText(treatment)}</strong>
                              </div>
                            ) : (
                              <strong>{treatment.name || "Unknown"}</strong>
                            )}
                            {treatment.isActive && (
                              <Badge bg="success" className="ms-2">Active</Badge>
                            )}
                            {!treatment.isActive && (
                              <Badge bg="secondary" className="ms-2">Inactive</Badge>
                            )}
                          </div>
                          <div className="small text-muted">
                            {treatment.startDate && (
                              <div>Start Date: {formatDate(treatment.startDate)}</div>
                            )}
                            {treatment.endDate && (
                              <div>End Date: {formatDate(treatment.endDate)}</div>
                            )}
                            {treatment.notes && (
                              <div className="mt-1">Notes: {treatment.notes}</div>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteClick("vivantaTreatment", treatment.userVivantaTreatmentId, treatment.name || "Unknown")}
                          className="ms-2"
                        >
                          ×
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <p className="text-muted mb-0">No vivanta treatments recorded</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Current Diseases */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-danger text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Current Diseases</h5>
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleOpenCurrentDiseaseModal}
                  style={{ fontSize: '1.2rem', lineHeight: '1', padding: '0.25rem 0.5rem' }}
                >
                  +
                </Button>
              </Card.Header>
              <Card.Body>
                {currentDiseases.length > 0 ? (
                  <ListGroup variant="flush">
                    {currentDiseases.map((disease) => (
                      <ListGroup.Item key={disease.id} className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div>
                            <strong>{disease.cie10?.name || "Unknown"}</strong>
                            {disease.cie10?.id && (
                              <span className="text-muted ms-2">(CIE10 ID: {disease.cie10.id})</span>
                            )}
                          </div>
                          {disease.notes && (
                            <div className="text-muted mt-1">
                              <small>Notes: {disease.notes}</small>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteClick("currentDisease", disease.id, disease.activeSubstance?.name || "Unknown")}
                          className="ms-2"
                        >
                          ×
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <p className="text-muted mb-0">No current diseases recorded</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Procedures */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-secondary text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Procedures</h5>
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleOpenProcedureModal}
                  style={{ fontSize: '1.2rem', lineHeight: '1', padding: '0.25rem 0.5rem' }}
                >
                  +
                </Button>
              </Card.Header>
              <Card.Body>
                {procedures.length > 0 ? (
                  <ListGroup variant="flush">
                    {procedures.map((procedure) => (
                      <ListGroup.Item key={procedure.id} className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div>
                            <strong>{procedure.cie9?.name || "Unknown"}</strong>
                            {procedure.cie9?.code && (
                              <Badge bg="info" className="ms-2">{procedure.cie9.code}</Badge>
                            )}
                          </div>
                          {procedure.dateOfProcedure && (
                            <div className="text-muted mt-1">
                              <small>Date: {formatDate(procedure.dateOfProcedure)}</small>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteClick("procedure", procedure.id, procedure.cie9?.name || "Unknown")}
                          className="ms-2"
                        >
                          ×
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <p className="text-muted mb-0">No procedures recorded</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Morbidities (Vivanta) */}
          {(
            <Col md={6} className="mb-4">
              <Card>
                <Card.Header className="bg-dark text-white">
                  <h5 className="mb-0">Morbidities (by Vivanta)</h5>
                </Card.Header>
                <Card.Body className="d-flex gap-2 flex-wrap">
                  {VIVANTA_MORBIDITIES.map((m) => {
                    const existingInProgram = programMorbidities.find(
                      (p) => p.morbidity.toLowerCase() === m.toLowerCase() && p.startDateOfDisease
                    );
                    const addedThisSession = addedMorbidities.includes(m);
                    const isDone = !!existingInProgram || addedThisSession;
                    return (
                      <Button
                        key={m}
                        variant={isDone ? "success" : "outline-secondary"}
                        disabled={!!morbiditySubmitting || isDone}
                        onClick={() => handleAddMorbidity(m)}
                      >
                        {morbiditySubmitting === m ? "Adding…" : isDone ? `✓ ${m}` : `+ ${m}`}
                      </Button>
                    );
                  })}
                </Card.Body>
              </Card>
            </Col>
          )}

          {/* Diet & Nutrition Section */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-danger text-white">
                <h5 className="mb-0">Diet & Nutrition</h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-2">
                  <Col sm={6}><strong>Diet Quality:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Diet Quality"
                      value={clinicalHistory.dietQuality}
                      type="select"
                      options={dietQualityOptions}
                      onSave={(value) => handleUpdateClinicalHistory("dietQuality", value)}
                      formatDisplay={(value) => {
                        if (value === null || value === undefined) return "Not specified";
                        const option = dietQualityOptions.find(opt => opt.value === value);
                        return option ? option.label : "Not specified";
                      }}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={6}><strong>Diet Quantity:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Diet Quantity"
                      value={clinicalHistory.dietQuantity}
                      type="select"
                      options={dietQuantityOptions}
                      onSave={(value) => handleUpdateClinicalHistory("dietQuantity", value)}
                      formatDisplay={(value) => {
                        if (value === null || value === undefined) return "Not specified";
                        const option = dietQuantityOptions.find(opt => opt.value === value);
                        return option ? option.label : "Not specified";
                      }}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={6}><strong>Meals per Day:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Meals per Day"
                      value={clinicalHistory.numberOfMealsADay}
                      type="number"
                      onSave={(value) => handleUpdateClinicalHistory("numberOfMealsADay", value)}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={6}><strong>Fruits per Week:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Fruits per Week"
                      value={clinicalHistory.numberOfFruitsAWeek}
                      type="number"
                      onSave={(value) => handleUpdateClinicalHistory("numberOfFruitsAWeek", value)}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={6}><strong>Vegetables per Week:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Vegetables per Week"
                      value={clinicalHistory.numberOfVegetablesAWeek}
                      type="number"
                      onSave={(value) => handleUpdateClinicalHistory("numberOfVegetablesAWeek", value)}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}><strong>Water per Day (L):</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Water per Day"
                      value={clinicalHistory.litersOfWaterADay}
                      type="number"
                      onSave={(value) => handleUpdateClinicalHistory("litersOfWaterADay", value)}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Lifestyle Section */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-secondary text-white">
                <h5 className="mb-0">Lifestyle</h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-2">
                  <Col sm={6}><strong>Alcohol Frequency (per week):</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Alcohol Frequency"
                      value={clinicalHistory.frequencyOfAlcoholAWeek}
                      type="number"
                      onSave={(value) => handleUpdateClinicalHistory("frequencyOfAlcoholAWeek", value)}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}><strong>Drug User:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Drug User"
                      value={clinicalHistory.isDrugUser === true ? 1 : (clinicalHistory.isDrugUser === false ? 0 : null)}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => {
                        const boolValue = value === 1 ? true : (value === 0 ? false : null);
                        return handleUpdateClinicalHistory("isDrugUser", boolValue);
                      }}
                      formatDisplay={(value) => {
                        const numValue = typeof value === 'string' ? parseInt(value) : value;
                        if (numValue === 1) return "Yes";
                        if (numValue === 0) return "No";
                        return "Not specified";
                      }}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Sleep Section */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header className="bg-dark text-white">
                <h5 className="mb-0">Sleep</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col sm={6}><strong>Sleep Treatment:</strong></Col>
                  <Col sm={6}>
                    <EditableField
                      label="Sleep Treatment"
                      value={clinicalHistory.hasSleepTreatment}
                      type="enum"
                      options={enumOptions}
                      onSave={(value) => {
                        return handleUpdateClinicalHistory("hasSleepTreatment", value);
                      }}
                      formatDisplay={formatEnumDisplay}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Other Allergy Modal */}
      <ClinicalHistoryCatalog
        show={showOtherAllergyModal}
        onHide={handleCloseOtherAllergyModal}
        title="Add Other Allergy"
        searchPlaceholder="Type to search allergens..."
        selectLabel="Allergen"
        items={[]}
        onSearch={handleSearchAllergens}
        onGetAll={handleGetAllAllergens}
        selectedItemId={selectedAllergenId}
        onSelectChange={setSelectedAllergenId}
        onSubmit={handleSubmitOtherAllergy}
        isSubmitting={isSubmitting}
        submitButtonText="Add Allergy"
        debounceDelay={750}
      />

      {/* Add Active Substance Allergy Modal */}
      <ClinicalHistoryCatalog
        show={showActiveSubstanceAllergyModal}
        onHide={handleCloseActiveSubstanceAllergyModal}
        title="Add Active Substance Allergy"
        searchPlaceholder="Type to search active substances..."
        selectLabel="Active Substance"
        items={[]}
        onSearch={handleSearchActiveSubstances}
        onGetAll={handleGetAllActiveSubstances}
        selectedItemId={selectedActiveSubstanceId}
        onSelectChange={setSelectedActiveSubstanceId}
        onSubmit={handleSubmitActiveSubstanceAllergy}
        isSubmitting={isSubmitting}
        submitButtonText="Add Allergy"
        debounceDelay={750}
      />

      {/* Add Active Substance Current Treatment Modal */}
      <ClinicalHistoryCatalog
        show={showActiveSubstanceTreatmentModal}
        onHide={handleCloseActiveSubstanceTreatmentModal}
        title="Add Current Treatment"
        searchPlaceholder="Type to search active substances..."
        selectLabel="Active Substance"
        items={[]}
        onSearch={handleSearchActiveSubstances}
        onGetAll={handleGetAllActiveSubstances}
        selectedItemId={selectedActiveSubstanceTreatmentId}
        onSelectChange={setSelectedActiveSubstanceTreatmentId}
        onSubmit={handleSubmitActiveSubstanceTreatment}
        isSubmitting={isSubmitting}
        submitButtonText="Add Treatment"
        debounceDelay={750}
      />

      {/* Add Food Allergy Modal */}
      <ClinicalHistoryCatalog
        show={showFoodAllergyModal}
        onHide={handleCloseFoodAllergyModal}
        title="Add Food Allergy"
        searchPlaceholder="Type to search foods..."
        selectLabel="Food"
        items={[]}
        onSearch={handleSearchFoods}
        onGetAll={handleGetAllFoods}
        selectedItemId={selectedFoodId}
        onSelectChange={setSelectedFoodId}
        onSubmit={handleSubmitFoodAllergy}
        isSubmitting={isSubmitting}
        submitButtonText="Add Allergy"
        debounceDelay={750}
      />

      {/* Add Current Disease Modal (with notes) */}
      <Modal show={showCurrentDiseaseModal} onHide={handleCloseCurrentDiseaseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add Current Disease</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Search Disease</Form.Label>
              <Form.Control
                type="text"
                placeholder="Type to search diseases..."
                onChange={(e) => {
                  const searchValue = e.target.value;
                  
                  // Clear any existing timeout
                  if (currentDiseaseSearchTimeoutRef.current) {
                    clearTimeout(currentDiseaseSearchTimeoutRef.current);
                  }
                  
                  // Set a new timeout to debounce the API call
                  currentDiseaseSearchTimeoutRef.current = setTimeout(() => {
                    if (searchValue.trim()) {
                      handleSearchCIE10(searchValue).then((result) => {
                        setCurrentDiseaseSubstances(result);
                      });
                    } else {
                      handleGetAllCIE10().then((result) => {
                        setCurrentDiseaseSubstances(result);
                      });
                    }
                    currentDiseaseSearchTimeoutRef.current = null;
                  }, debounceDelay);
                }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Select Disease</Form.Label>
              <Form.Select
                value={selectedActiveSubstanceDiseaseId || ""}
                onChange={(e) => setSelectedActiveSubstanceDiseaseId(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">Choose a disease...</option>
                {currentDiseaseSubstances.map((substance) => (
                  <option key={substance.id} value={substance.id}>
                    {substance.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCurrentDiseaseModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitCurrentDisease}
            disabled={!selectedCIE10Id || isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add Disease"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Procedure Modal (with date) */}
      <Modal show={showProcedureModal} onHide={handleCloseProcedureModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add Procedure</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Search CIE9</Form.Label>
              <Form.Control
                type="text"
                placeholder="Type to search CIE9..."
                onChange={(e) => {
                  const searchValue = e.target.value;
                  
                  // Clear any existing timeout
                  if (procedureSearchTimeoutRef.current) {
                    clearTimeout(procedureSearchTimeoutRef.current);
                  }
                  
                  // Set a new timeout to debounce the API call
                  procedureSearchTimeoutRef.current = setTimeout(() => {
                    if (searchValue.trim()) {
                      handleSearchCIE9(searchValue).then((result) => {
                        setProcedureCIE9s(result);
                      });
                    } else {
                      handleGetAllCIE9().then((result) => {
                        setProcedureCIE9s(result);
                      });
                    }
                    procedureSearchTimeoutRef.current = null;
                  }, debounceDelay);
                }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Select CIE9</Form.Label>
              <Form.Select
                value={selectedCIE9Id || ""}
                onChange={(e) => setSelectedCIE9Id(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">Choose a CIE9...</option>
                {procedureCIE9s.map((cie9) => (
                  <option key={cie9.id} value={cie9.id}>
                    {cie9.name} ({cie9.code})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Date of Procedure</Form.Label>
              <Form.Control
                type="date"
                value={procedureDate}
                onChange={(e) => setProcedureDate(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseProcedureModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitProcedure}
            disabled={!selectedCIE9Id || !procedureDate || isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add Procedure"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Heredofamiliar Disease Modal (complex form) */}
      <Modal show={showHeredofamiliarDiseaseModal} onHide={handleCloseHeredofamiliarDiseaseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Heredofamiliar Disease</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Search CIE10</Form.Label>
              <Form.Control
                type="text"
                placeholder="Type to search CIE10..."
                onChange={(e) => {
                  const searchValue = e.target.value;
                  
                  // Clear any existing timeout
                  if (heredofamiliarSearchTimeoutRef.current) {
                    clearTimeout(heredofamiliarSearchTimeoutRef.current);
                  }
                  
                  // Set a new timeout to debounce the API call
                  heredofamiliarSearchTimeoutRef.current = setTimeout(() => {
                    if (searchValue.trim()) {
                      handleSearchCIE10(searchValue).then((result) => {
                        setHeredofamiliarCIE10s(result);
                      });
                    } else {
                      handleGetAllCIE10().then((result) => {
                        setHeredofamiliarCIE10s(result);
                      });
                    }
                    heredofamiliarSearchTimeoutRef.current = null;
                  }, debounceDelay);
                }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Select CIE10</Form.Label>
              <Form.Select
                value={selectedCIE10Id || ""}
                onChange={(e) => setSelectedCIE10Id(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">Choose a CIE10...</option>
                {heredofamiliarCIE10s.map((cie10) => (
                  <option key={cie10.id} value={cie10.id}>
                    {cie10.name} ({cie10.code})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Children</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={heredofamiliarData.numberOfChildren}
                    onChange={(e) => setHeredofamiliarData({...heredofamiliarData, numberOfChildren: parseInt(e.target.value) || 0})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Siblings</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={heredofamiliarData.numberOfSiblings}
                    onChange={(e) => setHeredofamiliarData({...heredofamiliarData, numberOfSiblings: parseInt(e.target.value) || 0})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Paternal Grandfather</Form.Label>
              <Form.Select
                value={heredofamiliarData.isPaternalGrandfatherDiseased}
                onChange={(e) => setHeredofamiliarData({...heredofamiliarData, isPaternalGrandfatherDiseased: parseInt(e.target.value)})}
              >
                <option value={1}>Yes</option>
                <option value={0}>No</option>
                <option value={-1}>Don't know</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Paternal Grandmother</Form.Label>
              <Form.Select
                value={heredofamiliarData.isPaternalGrandmotherDiseased}
                onChange={(e) => setHeredofamiliarData({...heredofamiliarData, isPaternalGrandmotherDiseased: parseInt(e.target.value)})}
              >
                <option value={1}>Yes</option>
                <option value={0}>No</option>
                <option value={-1}>Don't know</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Maternal Grandfather</Form.Label>
              <Form.Select
                value={heredofamiliarData.isMaternalGrandfatherDiseased}
                onChange={(e) => setHeredofamiliarData({...heredofamiliarData, isMaternalGrandfatherDiseased: parseInt(e.target.value)})}
              >
                <option value={1}>Yes</option>
                <option value={0}>No</option>
                <option value={-1}>Don't know</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Maternal Grandmother</Form.Label>
              <Form.Select
                value={heredofamiliarData.isMaternalGrandmotherDiseased}
                onChange={(e) => setHeredofamiliarData({...heredofamiliarData, isMaternalGrandmotherDiseased: parseInt(e.target.value)})}
              >
                <option value={1}>Yes</option>
                <option value={0}>No</option>
                <option value={-1}>Don't know</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseHeredofamiliarDiseaseModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitHeredofamiliarDisease}
            disabled={!selectedCIE10Id || isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add Disease"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Vivanta Treatment Modal */}
      <Modal show={showVivantaTreatmentModal} onHide={handleCloseVivantaTreatmentModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add Vivanta Treatment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select Treatment</Form.Label>
              <Form.Select
                value={selectedVivantaTreatmentId || ""}
                onChange={(e) => setSelectedVivantaTreatmentId(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">Choose a treatment...</option>
                {vivantaTreatments.map((treatment) => (
                  <option key={treatment.id} value={treatment.id}>
                    {treatment.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={vivantaTreatmentFormData.startDate}
                onChange={(e) => setVivantaTreatmentFormData({...vivantaTreatmentFormData, startDate: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={vivantaTreatmentFormData.endDate}
                onChange={(e) => setVivantaTreatmentFormData({...vivantaTreatmentFormData, endDate: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Active</Form.Label>
              <Form.Select
                value={vivantaTreatmentFormData.isActive ? "1" : "0"}
                onChange={(e) => setVivantaTreatmentFormData({...vivantaTreatmentFormData, isActive: e.target.value === "1"})}
              >
                <option value="1">Yes</option>
                <option value="0">No</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={vivantaTreatmentFormData.notes}
                onChange={(e) => setVivantaTreatmentFormData({...vivantaTreatmentFormData, notes: e.target.value})}
                placeholder="Optional notes..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseVivantaTreatmentModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitVivantaTreatment}
            disabled={!selectedVivantaTreatmentId || !vivantaTreatmentFormData.startDate || !vivantaTreatmentFormData.endDate || !userId || isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add Treatment"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteConfirmModal.show} onHide={handleDeleteCancel}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>{deleteConfirmModal.name}</strong>?</p>
          <p className="text-muted">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default ClinicalHistoryPage;

